import { RefObject, useLayoutEffect, useRef, useState } from 'react';
import {
  AnchorRect,
  ClampedMenuPosition,
  clampAnchoredSubmenuToViewport,
  clampCursorMenuToViewport
} from 'utils/context-menu-position';

type UseClampedMenuPositionParams = Readonly<{
  x: number;
  y: number;
  anchor?: AnchorRect;
}>;

type UseClampedMenuPositionResult = {
  ref: RefObject<HTMLDivElement>;
  position: ClampedMenuPosition;
};

export default function useClampedMenuPosition({
  x,
  y,
  anchor
}: UseClampedMenuPositionParams): UseClampedMenuPositionResult {
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<ClampedMenuPosition>({ x, y });

  const anchorLeft = anchor?.left;
  const anchorRight = anchor?.right;
  const anchorTop = anchor?.top;
  const anchorBottom = anchor?.bottom;

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }

    const rect = element.getBoundingClientRect();
    const menuSize = { width: rect.width, height: rect.height };

    const hasAnchor =
      anchorLeft !== undefined &&
      anchorRight !== undefined &&
      anchorTop !== undefined &&
      anchorBottom !== undefined;

    const next = hasAnchor
      ? clampAnchoredSubmenuToViewport(
          {
            left: anchorLeft,
            right: anchorRight,
            top: anchorTop,
            bottom: anchorBottom
          },
          menuSize
        )
      : clampCursorMenuToViewport({ x, y }, menuSize);

    setPosition((prev) =>
      prev.x === next.x && prev.y === next.y ? prev : next
    );
  }, [x, y, anchorLeft, anchorRight, anchorTop, anchorBottom]);

  return { ref, position };
}
